import { useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useAuth } from '../../context/AuthContext'
import { createUser } from '../../services/api-admin'


const PHONE_RE = /^\d{10}$/; // Matches exactly 10 digits
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_UUID = 'GN000';

export default function Dashboard() {
  const { user, logout } = useAuth()

  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [uuid, setUuid] = useState(DEFAULT_UUID)
  const [channel, setChannel] = useState('whatsapp') // 'whatsapp' | 'email'
  const [status, setStatus] = useState(null) // { type: 'success'|'error', message }
  const [loading, setLoading] = useState(false)

  const handleReset = () => {
    setPhone('');
    setEmail('');
    setUuid(DEFAULT_UUID);
    setStatus(null);
  };

  function handleAutoUuid() {
    setUuid(uuidv4())
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setStatus(null)

    if (channel === 'whatsapp') {
      if (!PHONE_RE.test(phone)) {
        setStatus({ type: 'error', message: 'Phone must be exactly 10 digits.' })
        return
      }
    } else {
      if (!EMAIL_RE.test(email)) {
        setStatus({ type: 'error', message: 'Please enter a valid email address.' })
        return
      }
    }

    const fullPhoneNumber = channel === 'whatsapp' ? `+91${phone}` : null;

    setLoading(true)
    try {
      const data = await createUser(
        uuid,
        fullPhoneNumber,
        email === '' ? null : email
      );

      if (!data) {
        setStatus({ type: 'error', message: 'Failed to create user.' })
      } else {
        const dest = channel === 'whatsapp' ? phone : email;
        setStatus({
          type: 'success',
          message: channel === 'whatsapp'
            ? `WhatsApp message sent to +91${dest}`
            : `Magic link sent to ${dest}`
        })
        // setName('')
        setPhone('')
        setEmail('')
        setUuid(DEFAULT_UUID)
      }
    } catch (err) {
      console.error('Error creating user:', err);
      setStatus({ type: 'error', message: 'Network error — could not reach the server.' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="dash-shell">
      <header className="dash-header">
        <div className="dash-logo">STAFF PORTAL</div>
        <div className="dash-user-row">
          <span className="dash-email">{user?.email}</span>
          <button className="btn-ghost" onClick={logout}>
            Log out
          </button>
        </div>
      </header>

      <main className="dash-main">
        <div className="dash-card">
          <div className="dash-card-header">
            <img src="/images/icon.png" alt="Logo" className="auth-logo" />
            <button
              className="btn-reset-form"
              onClick={handleReset}
              title="Reset Form"
            >
              <span className="refresh-icon">↻</span>
            </button>
            <p className="dash-card-sub">Creates a user and dispatches a sign-in link via WhatsApp or Email.</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">

            {/* Channel toggle */}
            <div className="field-group">
              <label className="field-label">Send Via</label>
              <div className="channel-toggle">
                <button
                  type="button"
                  className={`channel-btn${channel === 'whatsapp' ? ' channel-btn--active' : ''}`}
                  onClick={() => { setChannel('whatsapp'); setStatus(null); }}
                >
                  WhatsApp
                </button>
                <button
                  type="button"
                  className={`channel-btn${channel === 'email' ? ' channel-btn--active' : ''}`}
                  onClick={() => { setChannel('email'); setStatus(null); }}
                >
                  Email
                </button>
              </div>
            </div>

            {/* Conditional contact field */}
            {channel === 'whatsapp' ? (
              <div className="field-group">
                <label className="field-label">Patient Phone Number</label>
                <div className="phone-input-combo">
                  <span className="phone-prefix">+91</span>
                  <span className="phone-divider">|</span>
                  <input
                    className="phone-main-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setPhone(val);
                    }}
                    required
                  />
                </div>
                <span className="field-hint">Enter the 10-digit mobile number</span>
              </div>
            ) : (
              <div className="field-group">
                <label className="field-label">Patient Email Address</label>
                <div className="phone-input-combo">
                  <input
                    className="phone-main-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="patient@example.com"
                    required
                  />
                </div>
                <span className="field-hint">Magic link will be sent to this address</span>
              </div>
            )}

            <div className="field-group">
              <label className="field-label">UUID</label>
              <div className="uuid-row">
                <input
                  className="field-input uuid-input"
                  type="text"
                  value={uuid}
                  onChange={(e) => setUuid(e.target.value)}
                  required
                  placeholder={DEFAULT_UUID}
                />
                <button
                  type="button"
                  className="btn-ghost btn-uuid"
                  onClick={handleAutoUuid}
                >
                  Auto-generate
                </button>
              </div>
            </div>

            {status && (
              <div className={`feedback feedback--${status.type}`}>
                {status.type === 'success' ? '✓ ' : '✕ '}
                {status.message}
              </div>
            )}

            <button className="btn-primary" type="submit" disabled={loading}>
              {loading ? 'Sending…' : 'Send Magic Link'}
            </button>
          </form>
        </div>
      </main>

      <style>{`
        .channel-toggle {
          display: flex;
          gap: 8px;
        }

        .channel-btn {
          display: flex;
          align-items: center;
          gap: 7px;
          flex: 1;
          justify-content: center;
          padding: 9px 14px;
          border-radius: 8px;
          border: 1.5px solid var(--border, #e2e8f0);
          background: transparent;
          color: var(--text-muted, #64748b);
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: border-color 0.15s, background 0.15s, color 0.15s;
        }

        .channel-btn:hover {
          border-color: var(--primary, #6366f1);
          color: var(--primary, #6366f1);
        }

        .channel-btn--active {
          border-color: var(--primary, #6366f1);
          background: var(--primary-light, #eef2ff);
          color: var(--primary, #6366f1);
        }
      `}</style>
    </div>
  )
}